import { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, API_URL, formatUSD, formatDate, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, Sparkles, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPTED = ".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.heic,.heif";

export default function ImportTab() {
  const { activeOrgId } = useAuth();
  const fileRef = useRef(null);
  const [filename, setFilename] = useState("");
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [parsed, setParsed] = useState([]); // [{date, description, amount, type, category_id, fund_id, raw, matched_existing_id, selected?}]
  const [categories, setCategories] = useState([]);
  const [funds, setFunds] = useState([]);

  const loadLookups = async () => {
    const [c, f] = await Promise.all([
      api.get(`/organizations/${activeOrgId}/categories`),
      api.get(`/organizations/${activeOrgId}/funds`),
    ]);
    setCategories(c.data);
    setFunds(f.data);
  };

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    setParsed([]);
    setParsing(true);
    try {
      await loadLookups();
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type_hint", "transactions");
      const token = localStorage.getItem("ledger_token") || "";
      const res = await fetch(`${API_URL}/organizations/${activeOrgId}/import/parse`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const items = (data.transactions || []).map((t) => ({ ...t, selected: !t.matched_existing_id }));
      setParsed(items);
      toast.success(`Parsed ${items.length} transactions from ${file.name}`);
    } catch (e) {
      toast.error(`Parse failed: ${e.message}`);
      setFilename("");
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggle = (idx) => {
    setParsed((p) => p.map((t, i) => i === idx ? { ...t, selected: !t.selected } : t));
  };
  const updateField = (idx, field, value) => {
    setParsed((p) => p.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };
  const removeRow = (idx) => {
    setParsed((p) => p.filter((_, i) => i !== idx));
  };

  const commitSelected = async () => {
    const selected = parsed.filter((t) => t.selected && t.amount > 0 && t.date);
    if (selected.length === 0) return toast.error("Nothing selected to import.");
    setCommitting(true);
    try {
      const payload = {
        transactions: selected.map((t) => ({
          date: t.date,
          description: t.description || "",
          amount: Number(t.amount),
          type: t.type,
          category_id: t.category_id || null,
          fund_id: t.fund_id || null,
        })),
      };
      const { data } = await api.post(`/organizations/${activeOrgId}/import/commit`, payload);
      toast.success(`Imported ${data.inserted} transactions to your ledger.`);
      setParsed([]);
      setFilename("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const selectedCount = parsed.filter((t) => t.selected).length;
  const totalIncome = parsed.filter((t) => t.selected && t.type === "income").reduce((s, t) => s + Number(t.amount || 0), 0);
  const totalExpense = parsed.filter((t) => t.selected && t.type === "expense").reduce((s, t) => s + Number(t.amount || 0), 0);

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm bg-card rounded-3xl p-7">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-serif text-2xl">AI-powered import</h3>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Upload a bank statement (<strong>PDF</strong> or <strong>photo</strong>), a CSV export from your bank, or an Excel
              file — ChurchFlow uses Gemini 3.1 Pro to extract every transaction, suggest categories and funds, and flag
              probable duplicates against your existing ledger. You review and approve before anything is saved.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            onChange={onFilePick}
            className="hidden"
            data-testid="import-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={parsing}
            className="rounded-2xl soft-glow h-12 px-6"
            data-testid="import-upload-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            {parsing ? `Parsing ${filename}…` : "Upload file"}
          </Button>
          <div className="text-xs text-muted-foreground mt-2">
            Accepted: PDF · PNG / JPG / HEIC photo · CSV · Excel (.xlsx)
          </div>
        </div>
      </Card>

      {parsing && (
        <Card className="border-0 shadow-sm bg-primary/5 rounded-3xl p-6 text-center">
          <Sparkles className="w-8 h-8 text-primary mx-auto animate-pulse" />
          <p className="font-serif text-xl mt-3">Reading your file…</p>
          <p className="text-sm text-muted-foreground mt-1">Gemini is extracting transactions. This usually takes 10-30 seconds.</p>
        </Card>
      )}

      {!parsing && parsed.length > 0 && (
        <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
          <div className="px-6 py-5 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-serif text-2xl flex items-center gap-2">
                <FileText className="w-5 h-5" /> Review &amp; import
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Uncheck rows you don't want. Edit the category / fund. Duplicates are pre-unchecked.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge variant="outline" className="bg-secondary text-foreground rounded-full border-0">{selectedCount} of {parsed.length} selected</Badge>
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 rounded-full">+{formatUSD(totalIncome)}</Badge>
              <Badge variant="outline" className="bg-expense/10 text-expense border-expense/20 rounded-full">−{formatUSD(totalExpense)}</Badge>
              <Button onClick={commitSelected} disabled={committing || selectedCount === 0} className="rounded-2xl soft-glow" data-testid="import-commit-button">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {committing ? "Importing…" : `Import ${selectedCount} →`}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-secondary/40">
                <tr>
                  <th className="px-3 py-2.5 text-left w-10"></th>
                  <th className="px-3 py-2.5 text-left">Date</th>
                  <th className="px-3 py-2.5 text-left">Description</th>
                  <th className="px-3 py-2.5 text-left">Type</th>
                  <th className="px-3 py-2.5 text-left">Category</th>
                  <th className="px-3 py-2.5 text-left">Fund</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody data-testid="parsed-table-body">
                {parsed.map((t, idx) => {
                  const cats = categories.filter((c) => c.type === t.type);
                  return (
                    <tr key={idx} className={`border-b border-border hover:bg-secondary/30 ${t.matched_existing_id ? "bg-amber-50/40" : ""}`}>
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={!!t.selected}
                          onChange={() => toggle(idx)}
                          data-testid={`parsed-checkbox-${idx}`}
                          className="w-4 h-4 rounded accent-primary"
                        />
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">{t.date || "—"}</td>
                      <td className="px-3 py-2.5 max-w-xs">
                        <div className="truncate">{t.description}</div>
                        {t.matched_existing_id && (
                          <div className="text-[10px] text-amber-700 mt-0.5 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Possible duplicate
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <Select value={t.type} onValueChange={(v) => updateField(idx, "type", v)}>
                          <SelectTrigger className="rounded-lg h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="income">Income</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2.5">
                        <Select value={t.category_id || "_none"} onValueChange={(v) => updateField(idx, "category_id", v === "_none" ? null : v)}>
                          <SelectTrigger className="rounded-lg h-8 w-40 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">—</SelectItem>
                            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2.5">
                        <Select value={t.fund_id || "_none"} onValueChange={(v) => updateField(idx, "fund_id", v === "_none" ? null : v)}>
                          <SelectTrigger className="rounded-lg h-8 w-40 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">—</SelectItem>
                            {funds.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className={`px-3 py-2.5 text-right font-mono tabular-nums ${t.type === "income" ? "text-primary" : "text-expense"}`}>
                        {t.type === "income" ? "+" : "−"}{formatUSD(t.amount)}
                      </td>
                      <td className="px-3 py-2.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeRow(idx)}
                          className="rounded-lg h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          data-testid={`parsed-remove-${idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
